import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { ApiError, api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { paginationSearchFields } from "@/lib/search";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/accounting/bank` — the statement and the payments inbox.
 *
 * Automatic matching only acts on a variable symbol in the document's
 * own currency, so what lands here is what a person has to decide:
 * payments with no symbol, symbols that match nothing, foreign-currency
 * payments, and movements that are not document payments at all. For
 * each one Hub offers what the payment could be — by symbol, by amount,
 * and by who has paid from that account before — but never places it
 * on its own.
 */

const searchSchema = z.object({
  view: z.enum(["inbox", "all", "ignored"]).optional(),
  ...paginationSearchFields,
});

type Match = {
  id: string;
  amount: string;
  source: "auto" | "manual" | "fakturoid";
  /** `false` = the movement only evidences the document; it was already paid in Fakturoid. */
  settles: boolean;
  issuedInvoice: { id: string; number: string; partnerName: string } | null;
  receivedInvoice: {
    id: string;
    number: string | null;
    internalNumber: string | null;
    partnerName: string;
  } | null;
};

type Transaction = {
  id: string;
  bookedOn: string;
  amount: string;
  currency: string;
  variableSymbol: string | null;
  counterpartyName: string | null;
  counterpartyAccount: string | null;
  counterpartyBankCode: string | null;
  userIdentification: string | null;
  message: string | null;
  ignoredAt: string | null;
  ignoreReason: string | null;
  /** Put aside by a rule rather than by a person. */
  ignoredByRuleId: string | null;
  account: { label: string };
  matches: Match[];
  /** Merchant and the charge before conversion, read from a card payment's text. */
  card: { merchant: string; amount: string; currency: string } | null;
};

type Candidate = {
  kind: "issued" | "received";
  id: string;
  number: string;
  partnerName: string;
  total: string;
  currency: string;
  status: string;
  reasons: Array<
    "variable_symbol" | "exact_amount" | "known_counterparty" | "card_amount" | "supplier_name"
  >;
};

type SyncResult = {
  accounts?: number;
  imported?: number;
  matched?: number;
  unmatched?: number;
  skipped?: Array<{ account: string; reason: string }>;
  logs?: string[];
};

type BankStatus = {
  lastRun: {
    status: "pending" | "running" | "completed" | "failed";
    startedAt: string | null;
    finishedAt: string | null;
    error: string | null;
    result: SyncResult | null;
  } | null;
  accountsWithToken: number;
};

type BankAccountView = {
  id: string;
  label: string;
  accountNumber: string;
  currency: string;
  pairingEnabled: boolean;
  syncMode: "window" | "bookmark";
  lastFetchedAt: string | null;
  hasToken: boolean;
};

export const Route = createFileRoute("/_app/accounting/bank")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    view: search.view,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 50,
  }),
  loader: async ({ deps }) => {
    const view = deps.view ?? "inbox";
    const query = view === "inbox" ? "&matched=false" : view === "ignored" ? "&ignored=true" : "";
    try {
      const [movements, accounts, status] = await Promise.all([
        api.get<{ transactions: Transaction[]; total: number; page: number; pageSize: number }>(
          `/api/accounting/bank/transactions?page=${deps.page}&pageSize=${deps.pageSize}${query}`,
        ),
        api.get<{ accounts: BankAccountView[] }>("/api/accounting/bank/accounts"),
        api.get<BankStatus>("/api/accounting/bank/status"),
      ]);
      return { ...movements, accounts: accounts.accounts, status, view };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: BankPage,
});

function money(amount: string, currency: string): string {
  return `${formatNumber(Number(amount), undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function BankPage() {
  const { t } = useTranslation();
  const { transactions, total, page, pageSize, accounts, status, view } = Route.useLoaderData();
  const search = Route.useSearch();
  // Pulling and backfilling only make sense where there is something to
  // read. Without a token both would just occupy the queue and return
  // nothing.
  const canPull = status.accountsWithToken > 0;
  const navigate = useNavigate();
  const router = useRouter();
  const [open, setOpen] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const pull = useApiMutation(
    (body: { from?: string; to?: string }) => api.post("/api/accounting/bank/sync/run", body),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accBank.errors.pull") },
  );

  const [rematched, setRematched] = useState<{
    checked: number;
    matched: number;
    released: number;
    restored: number;
  } | null>(null);
  const rematch = useApiMutation(
    () =>
      api.post<{ checked: number; matched: number; released: number; restored: number }>(
        "/api/accounting/bank/rematch",
        {},
      ),
    {
      onSuccess: (result) => {
        setRematched(result);
        router.invalidate();
      },
      errorMessage: () => t("accBank.errors.rematch"),
    },
  );

  const addAccount = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/bank/accounts", body),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accBank.errors.addAccount") },
  );

  const removeAccount = useApiMutation(
    (id: string) => api.delete(`/api/accounting/bank/accounts/${id}`),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accBank.errors.removeAccount") },
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accBank.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("accBank.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          disabled={pull.pending || !canPull}
          title={canPull ? undefined : t("accBank.needToken")}
          onClick={() => pull.mutate({})}
        >
          {pull.pending ? t("accBank.queueing") : t("accBank.pullNow")}
        </Button>
      </div>

      {pull.error && <p className="text-destructive text-sm">{pull.error}</p>}
      {removeAccount.error && <p className="text-destructive text-sm">{removeAccount.error}</p>}

      <LastPull status={status} accountCount={accounts.length} />

      <Card>
        <CardHeader>
          <CardTitle>{t("accBank.accounts.title")}</CardTitle>
          <CardDescription>{t("accBank.accounts.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {accounts.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("accBank.accounts.empty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accBank.accounts.account")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.accounts.number")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.accounts.token")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.accounts.reads")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.accounts.autoMatching")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.accounts.lastPull")}</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id} className="border-t">
                    <td className="py-1 pr-4">{account.label}</td>
                    <td className="py-1 pr-4 font-mono text-xs">{account.accountNumber}</td>
                    <td className="py-1 pr-4">
                      <TokenCell account={account} onDone={() => router.invalidate()} />
                    </td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {account.syncMode === "window"
                        ? t("accBank.accounts.last30")
                        : t("accBank.accounts.sinceBookmark")}
                    </td>
                    <td className="py-1 pr-4">
                      {account.pairingEnabled
                        ? t("accBank.accounts.on")
                        : t("accBank.accounts.off")}
                    </td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {account.lastFetchedAt ? (
                        <LocalDate value={account.lastFetchedAt} withTime />
                      ) : (
                        t("accBank.accounts.never")
                      )}
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        className="text-destructive text-xs underline"
                        disabled={removeAccount.pending}
                        onClick={() => removeAccount.mutate(account.id)}
                      >
                        {t("accBank.accounts.remove")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <form
            className="grid gap-3 border-t pt-4 sm:grid-cols-6"
            onSubmit={(event) => {
              event.preventDefault();
              const fd = new FormData(event.currentTarget);
              const text = (key: string) => ((fd.get(key) as string) ?? "").trim();
              addAccount.mutate({
                label: text("label"),
                accountNumber: text("accountNumber"),
                bankCode: text("bankCode") || null,
                currency: text("currency") || "CZK",
                // The token goes to the server in this one request only;
                // it never comes back.
                fioToken: text("fioToken") || null,
              });
              event.currentTarget.reset();
            }}
          >
            <div className="sm:col-span-2">
              <label htmlFor="label" className="text-muted-foreground text-xs">
                {t("accBank.accounts.accountName")}
              </label>
              <input
                id="label"
                name="label"
                required
                placeholder={t("accBank.accounts.accountNamePlaceholder")}
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="accountNumber" className="text-muted-foreground text-xs">
                {t("accBank.accounts.accountNumber")}
              </label>
              <input
                id="accountNumber"
                name="accountNumber"
                required
                placeholder="2900233333"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="bankCode" className="text-muted-foreground text-xs">
                {t("accBank.accounts.bankCode")}
              </label>
              <input
                id="bankCode"
                name="bankCode"
                placeholder="2010"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="currency" className="text-muted-foreground text-xs">
                {t("accBank.accounts.currency")}
              </label>
              <input
                id="currency"
                name="currency"
                defaultValue="CZK"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div className="sm:col-span-5">
              <label htmlFor="fioToken" className="text-muted-foreground text-xs">
                {t("accBank.accounts.fioToken")}
              </label>
              <input
                id="fioToken"
                name="fioToken"
                type="password"
                autoComplete="off"
                placeholder={t("accBank.accounts.fioTokenPlaceholder")}
                className="block h-9 w-full rounded-md border border-input bg-background px-2 font-mono text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" disabled={addAccount.pending}>
                {addAccount.pending ? t("accBank.accounts.saving") : t("accBank.accounts.add")}
              </Button>
            </div>
            {addAccount.error && (
              <p className="text-destructive text-sm sm:col-span-6">{addAccount.error}</p>
            )}
          </form>

          <div className="flex flex-wrap items-end gap-2 border-t pt-4">
            <div>
              <label htmlFor="backfill-from" className="text-muted-foreground text-xs">
                {t("accBank.accounts.backfillFrom")}
              </label>
              <input
                id="backfill-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="backfill-to" className="text-muted-foreground text-xs">
                {t("accBank.accounts.backfillTo")}
              </label>
              <input
                id="backfill-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <Button
              variant="outline"
              disabled={!from || !to || pull.pending || !canPull}
              title={canPull ? undefined : t("accBank.needToken")}
              onClick={() => pull.mutate({ from, to })}
            >
              {t("accBank.accounts.fetchHistory")}
            </Button>
            <p className="text-muted-foreground text-xs">{t("accBank.accounts.historyHint")}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              {view === "inbox"
                ? t("accBank.movements.titleInbox")
                : view === "ignored"
                  ? t("accBank.movements.titleIgnored")
                  : t("accBank.movements.titleAll")}
            </CardTitle>
            <CardDescription>{t("accBank.movements.count", { total })}</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {view === "inbox" && (
              <>
                {rematched && (
                  <span className="text-muted-foreground text-xs">
                    {t("accBank.movements.placedOf", {
                      matched: rematched.matched,
                      checked: rematched.checked,
                    })}
                    {rematched.released > 0 &&
                      t("accBank.movements.releasedNote", { count: rematched.released })}
                    {rematched.restored > 0 &&
                      t("accBank.movements.restoredNote", { count: rematched.restored })}
                  </span>
                )}
                {rematch.error && <span className="text-destructive text-xs">{rematch.error}</span>}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rematch.pending}
                  onClick={() => rematch.mutate()}
                >
                  {rematch.pending
                    ? t("accBank.movements.matching")
                    : t("accBank.movements.matchAgain")}
                </Button>
              </>
            )}
            {(["inbox", "all", "ignored"] as const).map((option) => (
              <Button
                key={option}
                variant={view === option ? "default" : "outline"}
                size="sm"
                onClick={() => navigate({ to: "/accounting/bank", search: { view: option } })}
              >
                {option === "inbox"
                  ? t("accBank.movements.viewInbox")
                  : option === "all"
                    ? t("accBank.movements.viewAll")
                    : t("accBank.movements.viewIgnored")}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {view === "inbox" ? t("accBank.movements.emptyInbox") : t("accBank.movements.empty")}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accBank.movements.date")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.movements.counterparty")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.movements.vs")}</th>
                  <th className="py-1 pr-4 text-right font-medium">
                    {t("accBank.movements.amount")}
                  </th>
                  <th className="py-1 font-medium">{t("accBank.movements.status")}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((movement) => (
                  <MovementRow
                    key={movement.id}
                    movement={movement}
                    isOpen={open === movement.id}
                    onToggle={() => setOpen(open === movement.id ? null : movement.id)}
                    onDone={() => {
                      setOpen(null);
                      router.invalidate();
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            total={total}
            page={page}
            pageSize={pageSize}
            pageSizes={PAGE_SIZE_OPTIONS}
            storageKey="accounting-bank"
            basePath="/accounting/bank"
            searchParams={{ view: search.view, pageSize: search.pageSize }}
          />
        </CardContent>
      </Card>

      <PaidCheck />

      <RebuildFromBank />

      <IgnoreRules />
    </div>
  );
}

type CheckRow = {
  kind: "issued" | "received";
  id: string;
  number: string;
  partnerName: string;
  total: string;
  currency: string;
  paidOn: string | null;
  verdict: "tied" | "fits" | "no_movement";
  movements: Array<{
    id: string;
    bookedOn: string;
    amount: string;
    currency: string;
    text: string | null;
  }>;
};

/**
 * Whether the bank backs up what the books call paid. Fakturoid paired
 * its own payments; a document it calls paid with nothing on the account
 * that fits is paid in cash, from elsewhere, or paired wrongly.
 */
function PaidCheck() {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<CheckRow[] | null>(null);

  const run = useApiMutation(
    () => api.get<{ rows: CheckRow[] }>(`/api/accounting/bank/check?year=${year}`),
    { onSuccess: (result) => setRows(result.rows), errorMessage: () => t("accBank.errors.check") },
  );
  const tie = useApiMutation(
    ({ row, movementId }: { row: CheckRow; movementId: string }) =>
      api.post(
        `/api/accounting/bank/transactions/${movementId}/match`,
        row.kind === "received" ? { receivedInvoiceId: row.id } : { issuedInvoiceId: row.id },
      ),
    {
      onSuccess: async () => {
        await run.mutate();
      },
      errorMessage: () => t("accBank.errors.tie"),
    },
  );

  const count = (verdict: CheckRow["verdict"]) =>
    rows?.filter((r) => r.verdict === verdict).length ?? 0;
  const open = rows?.filter((r) => r.verdict !== "tied") ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle>{t("accBank.check.title")}</CardTitle>
          <CardDescription>{t("accBank.check.description")}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            aria-label={t("accBank.check.year")}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
          />
          <Button variant="outline" size="sm" disabled={run.pending} onClick={() => run.mutate()}>
            {run.pending ? t("accBank.check.checking") : t("accBank.check.check")}
          </Button>
        </div>
      </CardHeader>
      {(rows || run.error) && (
        <CardContent className="space-y-3">
          {run.error && <p className="text-destructive text-sm">{run.error}</p>}
          {rows && (
            <p className="text-sm">
              <span className="font-medium">{count("tied")}</span> {t("accBank.check.tied")} ·{" "}
              <span className="font-medium">{count("fits")}</span> {t("accBank.check.fits")} ·{" "}
              <span className="font-medium text-amber-700 dark:text-amber-400">
                {count("no_movement")}
              </span>{" "}
              {t("accBank.check.noMovement")}
            </p>
          )}
          {open.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accBank.check.paid")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.check.document")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accBank.check.total")}</th>
                  <th className="py-1 font-medium">{t("accBank.check.inBank")}</th>
                </tr>
              </thead>
              <tbody>
                {open.map((row) => (
                  <tr key={`${row.kind}-${row.id}`} className="border-t align-top">
                    <td className="py-1 pr-4 text-muted-foreground">
                      {row.paidOn ? <LocalDate value={row.paidOn} /> : "—"}
                    </td>
                    <td className="py-1 pr-4">
                      {row.kind === "received"
                        ? t("accBank.doc.expense")
                        : t("accBank.doc.invoice")}{" "}
                      {row.number}
                      <div className="text-muted-foreground text-xs">{row.partnerName}</div>
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {money(row.total, row.currency)}
                    </td>
                    <td className="py-1">
                      {row.verdict === "no_movement" ? (
                        <Badge variant="outline">{t("accBank.check.nothingFits")}</Badge>
                      ) : (
                        <ul className="space-y-1">
                          {row.movements.map((m) => (
                            <li key={m.id} className="flex flex-wrap items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={tie.pending}
                                onClick={() => tie.mutate({ row, movementId: m.id })}
                              >
                                {t("accBank.check.tie")}
                              </Button>
                              <LocalDate value={m.bookedOn} /> · {money(m.amount, m.currency)}
                              {m.text && (
                                <span className="text-muted-foreground text-xs">{m.text}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tie.error && <p className="text-destructive text-sm">{tie.error}</p>}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * What the last pull did.
 *
 * "I clicked Pull now and nothing happened" truthfully describes three
 * different things: no account with a token, Fio returned nothing, or
 * the run failed. The page has to tell them apart itself — looking into
 * /sync
 * nikdo nebude.
 */
function LastPull({ status, accountCount }: { status: BankStatus; accountCount: number }) {
  const { t } = useTranslation();
  const run = status.lastRun;

  if (status.accountsWithToken === 0) {
    return (
      <p className="rounded-md bg-amber-50 p-3 text-amber-900 text-sm dark:bg-amber-950/40 dark:text-amber-200">
        {accountCount === 0 ? t("accBank.lastPull.noAccount") : t("accBank.lastPull.noToken")}
      </p>
    );
  }

  if (!run) {
    return <p className="text-muted-foreground text-sm">{t("accBank.lastPull.none")}</p>;
  }

  if (run.status === "failed") {
    return (
      <p className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
        {t("accBank.lastPull.failed", { error: run.error ?? t("accBank.lastPull.noDetails") })}
      </p>
    );
  }

  if (run.status === "running" || run.status === "pending") {
    return <p className="text-muted-foreground text-sm">{t("accBank.lastPull.running")}</p>;
  }

  const result = run.result ?? {};
  const skipped = result.skipped ?? [];

  return (
    <div className="space-y-1 rounded-md border p-3 text-sm">
      <div>
        {run.finishedAt ? (
          <Trans
            i18nKey="accBank.lastPull.summary"
            values={{ imported: result.imported ?? 0, matched: result.matched ?? 0 }}
            components={{ 0: <LocalDate value={run.finishedAt} withTime />, 1: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="accBank.lastPull.summaryNoDate"
            values={{ imported: result.imported ?? 0, matched: result.matched ?? 0 }}
            components={{ 1: <strong /> }}
          />
        )}
      </div>
      {(result.imported ?? 0) === 0 && skipped.length === 0 && (
        // An empty result is a normal state, not an error — but it has to
        // be said, otherwise it looks like a fault.
        <div className="text-muted-foreground">{t("accBank.lastPull.nothingNew")}</div>
      )}
      {skipped.map((entry) => (
        <div key={`${entry.account}-${entry.reason}`} className="text-amber-600">
          {entry.account}: {entry.reason}
        </div>
      ))}
    </div>
  );
}

/** The token is never shown, not even shortened — the only thing that
 *  can be said about it is whether it is set. It can be replaced at any
 *  time (Fio invalidates it after 180 days), which overwrites the old
 *  one. */
function TokenCell({
  account,
  onDone,
}: {
  account: BankAccountView;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [token, setToken] = useState("");

  const save = useApiMutation(
    (value: string) =>
      api.patch(`/api/accounting/bank/accounts/${account.id}`, { fioToken: value }),
    {
      onSuccess: () => {
        setToken("");
        setEditing(false);
        onDone();
      },
      errorMessage: () => t("accBank.errors.saveToken"),
    },
  );

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        {account.hasToken ? (
          <Badge variant="success">{t("accBank.token.set")}</Badge>
        ) : (
          <Badge variant="outline">{t("accBank.token.missing")}</Badge>
        )}
        <button
          type="button"
          className="text-muted-foreground text-xs underline"
          onClick={() => setEditing(true)}
        >
          {account.hasToken ? t("accBank.token.replace") : t("accBank.token.add")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="password"
        autoComplete="off"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder={t("accBank.token.placeholder")}
        className="h-8 w-44 rounded-md border border-input bg-background px-2 font-mono text-xs"
      />
      <Button
        size="sm"
        disabled={token.trim().length < 16 || save.pending}
        onClick={() => save.mutate(token.trim())}
      >
        {t("accBank.token.save")}
      </Button>
      <button
        type="button"
        className="text-muted-foreground text-xs underline"
        onClick={() => {
          setToken("");
          setEditing(false);
        }}
      >
        {t("accBank.token.cancel")}
      </button>
      {save.error && <span className="text-destructive text-xs">{save.error}</span>}
    </div>
  );
}

function MovementRow({
  movement,
  isOpen,
  onToggle,
  onDone,
}: {
  movement: Transaction;
  isOpen: boolean;
  onToggle: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [reason, setReason] = useState("");

  // Suggestions are only worth fetching for the movement a person
  // actually opened.
  useEffect(() => {
    if (!isOpen || candidates !== null) return;
    api
      .get<{ candidates: Candidate[] }>(
        `/api/accounting/bank/transactions/${movement.id}/candidates`,
      )
      .then((res) => setCandidates(res.candidates))
      .catch(() => setCandidates([]));
  }, [isOpen, candidates, movement.id]);

  const place = useApiMutation(
    (candidate: Candidate) =>
      api.post(
        `/api/accounting/bank/transactions/${movement.id}/match`,
        candidate.kind === "received"
          ? { receivedInvoiceId: candidate.id }
          : { issuedInvoiceId: candidate.id },
      ),
    { onSuccess: onDone, errorMessage: () => t("accBank.errors.place") },
  );

  const revert = useApiMutation(
    (matchId: string) => api.post(`/api/accounting/bank/matches/${matchId}/revert`, {}),
    { onSuccess: onDone, errorMessage: () => t("accBank.errors.revert") },
  );

  const [always, setAlways] = useState(false);
  const putAside = useApiMutation(
    (why: string) =>
      api.post(`/api/accounting/bank/transactions/${movement.id}/ignore`, {
        reason: why,
        always,
      }),
    { onSuccess: onDone, errorMessage: () => t("accBank.errors.putAside") },
  );

  const bringBack = useApiMutation(
    () => api.post(`/api/accounting/bank/transactions/${movement.id}/unignore`, {}),
    { onSuccess: onDone, errorMessage: () => t("accBank.errors.bringBack") },
  );

  const incoming = Number(movement.amount) > 0;

  return (
    <>
      <tr className="border-t">
        <td className="py-1 pr-4 text-muted-foreground">
          <LocalDate value={movement.bookedOn} />
        </td>
        <td className="py-1 pr-4">
          {movement.counterpartyName ??
            (movement.card ? (
              <span className="flex items-center gap-1">
                {movement.card.merchant}
                <Badge variant="outline" className="text-[10px]">
                  {t("accBank.movements.card")}
                </Badge>
              </span>
            ) : (
              (movement.userIdentification ??
              (movement.counterpartyAccount ? (
                <span className="font-mono text-xs">
                  {movement.counterpartyAccount}
                  {movement.counterpartyBankCode && `/${movement.counterpartyBankCode}`}
                </span>
              ) : (
                "—"
              )))
            ))}
          {movement.message && (
            <div className="text-muted-foreground text-xs">{movement.message}</div>
          )}
        </td>
        <td className="py-1 pr-4 font-mono text-xs">{movement.variableSymbol ?? "—"}</td>
        <td
          className={`py-1 pr-4 text-right tabular-nums ${incoming ? "text-emerald-600" : "text-muted-foreground"}`}
        >
          {money(movement.amount, movement.currency)}
          {movement.card && movement.card.currency !== movement.currency && (
            <div className="text-muted-foreground text-xs">
              {money(movement.card.amount, movement.card.currency)}
            </div>
          )}
        </td>
        <td className="py-1">
          {movement.matches.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {movement.matches.map((m) => (
                <span key={m.id} className="flex items-center gap-1">
                  <Badge variant={m.source === "auto" ? "secondary" : "outline"}>
                    {m.issuedInvoice?.number ??
                      m.receivedInvoice?.number ??
                      m.receivedInvoice?.internalNumber ??
                      m.receivedInvoice?.partnerName ??
                      "?"}{" "}
                    ·{" "}
                    {m.source === "fakturoid"
                      ? t("accBank.movements.sourceFakturoid")
                      : m.source === "auto"
                        ? t("accBank.movements.sourceAuto")
                        : t("accBank.movements.sourceManual")}
                    {m.settles ? "" : t("accBank.movements.onlyLink")}
                  </Badge>
                  <button
                    type="button"
                    className="text-muted-foreground text-xs underline"
                    disabled={revert.pending}
                    onClick={() => revert.mutate(m.id)}
                  >
                    {t("accBank.movements.undo")}
                  </button>
                </span>
              ))}
            </div>
          ) : movement.ignoredAt ? (
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {movement.ignoredByRuleId
                  ? t("accBank.movements.putAsideRule")
                  : t("accBank.movements.putAside")}
              </Badge>
              <span className="text-muted-foreground text-xs">{movement.ignoreReason}</span>
              <button
                type="button"
                className="text-muted-foreground text-xs underline"
                disabled={bringBack.pending}
                onClick={() => bringBack.mutate()}
              >
                {t("accBank.movements.bringBack")}
              </button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={onToggle}>
              {isOpen ? t("accBank.movements.close") : t("accBank.movements.decide")}
            </Button>
          )}
        </td>
      </tr>

      {isOpen && (
        <tr className="border-t bg-muted/30">
          <td colSpan={5} className="space-y-3 p-3">
            <div>
              <div className="mb-1 text-muted-foreground text-xs">
                {t("accBank.movements.couldBe")}
              </div>
              {candidates === null ? (
                <p className="text-muted-foreground text-sm">{t("accBank.movements.looking")}</p>
              ) : candidates.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("accBank.movements.noCandidates")}
                </p>
              ) : (
                <ul className="space-y-1">
                  {candidates.map((candidate) => (
                    <li key={candidate.id} className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        disabled={place.pending}
                        onClick={() => place.mutate(candidate)}
                      >
                        {candidate.status === "paid"
                          ? t("accBank.movements.tieTo", { number: candidate.number })
                          : t("accBank.movements.placeOn", { number: candidate.number })}
                      </Button>
                      <span className="text-sm">
                        {candidate.partnerName} · {money(candidate.total, candidate.currency)}
                      </span>
                      {candidate.reasons.map((r) => (
                        <Badge key={r} variant="secondary" className="text-[10px]">
                          {t(`accBank.reason.${r}`)}
                        </Badge>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <input
                type="text"
                placeholder={t("accBank.movements.reasonPlaceholder")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-9 min-w-[18rem] flex-1 rounded-md border border-input bg-background px-2 text-sm"
              />
              {(movement.counterpartyAccount || (movement.card && Number(movement.amount) < 0)) && (
                <label
                  className="flex items-center gap-1 text-xs"
                  htmlFor={`always-${movement.id}`}
                >
                  <input
                    id={`always-${movement.id}`}
                    type="checkbox"
                    checked={always}
                    onChange={(e) => setAlways(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {movement.card
                    ? t("accBank.movements.alwaysCard", { merchant: movement.card.merchant })
                    : t("accBank.movements.alwaysAccount", {
                        account: movement.counterpartyAccount,
                        bankCode: movement.counterpartyBankCode,
                      })}
                </label>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={reason.trim().length < 2 || putAside.pending}
                onClick={() => putAside.mutate(reason.trim())}
              >
                {t("accBank.movements.putAsideButton")}
              </Button>
              {(place.error || putAside.error || revert.error) && (
                <span className="text-destructive text-sm">
                  {place.error ?? putAside.error ?? revert.error}
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

type IgnoreRule = {
  id: string;
  counterpartyAccount: string | null;
  counterpartyBankCode: string | null;
  cardMerchant: string | null;
  reason: string;
  putAside: number;
};

/** Accounts whose payments are never a document payment: taxes, donations. */
function IgnoreRules() {
  const { t } = useTranslation();
  const router = useRouter();
  const [rules, setRules] = useState<IgnoreRule[] | null>(null);
  const load = useCallback(
    () =>
      api
        .get<{ rules: IgnoreRule[] }>("/api/accounting/bank/ignore-rules")
        .then((r) => setRules(r.rules))
        .catch(() => setRules([])),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const remove = useApiMutation(
    (id: string) => api.delete(`/api/accounting/bank/ignore-rules/${id}`),
    {
      onSuccess: async () => {
        await load();
        router.invalidate();
      },
      errorMessage: () => t("accBank.errors.removeRule"),
    },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("accBank.rules.title")}</CardTitle>
        <CardDescription>{t("accBank.rules.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {rules === null ? (
          <p className="text-muted-foreground text-sm">{t("accBank.rules.loading")}</p>
        ) : rules.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("accBank.rules.none")}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rules.map((rule) => (
              <li key={rule.id} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">
                  {rule.cardMerchant
                    ? t("accBank.rules.card", { merchant: rule.cardMerchant })
                    : `${rule.counterpartyAccount ?? t("accBank.rules.anyAccount")}/${rule.counterpartyBankCode}`}
                </span>
                <span>{rule.reason}</span>
                <span className="text-muted-foreground text-xs">
                  {t("accBank.rules.putAsideCount", { count: rule.putAside })}
                </span>
                <button
                  type="button"
                  className="text-muted-foreground text-xs underline"
                  disabled={remove.pending}
                  onClick={() => remove.mutate(rule.id)}
                >
                  {t("accBank.rules.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
        {remove.error && <p className="text-destructive text-sm">{remove.error}</p>}
      </CardContent>
    </Card>
  );
}

type RebuildState = {
  run: { id: string; year: number; createdAt: string; documents: number } | null;
  unconfirmed: Array<{
    kind: "issued" | "received";
    id: string;
    number: string;
    partnerName: string;
    total: string;
    paidInHub: string;
    currency: string;
    issuedOn: string;
    fakturoidPayments: Array<{ paidOn: string; amount: string }>;
    bankCandidates: Array<{
      transactionId: string;
      bookedOn: string;
      amount: string;
      currency: string;
      payee: string | null;
      card: { merchant: string; amount: string; currency: string } | null;
      reasons: string[];
      parts: Array<{ transactionId: string }> | null;
    }>;
  }>;
  accepted: Array<{
    kind: "issued" | "received";
    id: string;
    number: string;
    partnerName: string;
    total: string;
    currency: string;
  }>;
};

/**
 * Fakturoid's pairing can't be trusted: the bank decides what is paid.
 * Fakturoid itself is left as it is.
 */
function RebuildFromBank() {
  const { t } = useTranslation();
  const router = useRouter();
  const [year, setYear] = useState(new Date().getFullYear());
  const [state, setState] = useState<RebuildState | null>(null);
  const load = useCallback(
    () =>
      api
        .get<RebuildState>("/api/accounting/bank/rebuild")
        .then(setState)
        .catch(() => setState({ run: null, unconfirmed: [], accepted: [] })),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const after = async () => {
    await load();
    router.invalidate();
  };

  const rebuild = useApiMutation(() => api.post("/api/accounting/bank/rebuild", { year }), {
    onSuccess: after,
    errorMessage: () => t("accBank.errors.rebuild"),
  });
  const accept = useApiMutation(
    ({ runId, kind, documentId }: { runId: string; kind: string; documentId: string }) =>
      api.post(`/api/accounting/bank/rebuild/${runId}/accept`, { kind, documentId }),
    { onSuccess: after, errorMessage: () => t("accBank.errors.accept") },
  );
  const unaccept = useApiMutation(
    ({ runId, kind, documentId }: { runId: string; kind: string; documentId: string }) =>
      api.post(`/api/accounting/bank/rebuild/${runId}/unaccept`, { kind, documentId }),
    { onSuccess: after, errorMessage: () => t("accBank.errors.unaccept") },
  );
  const pair = useApiMutation(
    async ({ kind, documentId, ids }: { kind: string; documentId: string; ids: string[] }) => {
      for (const id of ids) {
        await api.post(`/api/accounting/bank/transactions/${id}/match`, {
          [kind === "issued" ? "issuedInvoiceId" : "receivedInvoiceId"]: documentId,
        });
      }
    },
    { onSuccess: after, fallbackMessage: t("accBank.errors.pair") },
  );
  const undo = useApiMutation(
    (runId: string) => api.post(`/api/accounting/bank/rebuild/${runId}/revert`, {}),
    { onSuccess: after, errorMessage: () => t("accBank.errors.undoRebuild") },
  );

  const run = state?.run;
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle>{t("accBank.rebuild.title")}</CardTitle>
          <CardDescription>{t("accBank.rebuild.description")}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            aria-label={t("accBank.rebuild.yearLabel")}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={rebuild.pending}
            onClick={() => {
              if (window.confirm(t("accBank.rebuild.confirm", { year }))) {
                rebuild.mutate();
              }
            }}
          >
            {rebuild.pending ? t("accBank.rebuild.rebuilding") : t("accBank.rebuild.button")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {run ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>
              <Trans
                i18nKey="accBank.rebuild.last"
                values={{ year: run.year, documents: run.documents }}
                components={{ 0: <LocalDate value={run.createdAt} withTime /> }}
              />
            </span>
            <button
              type="button"
              className="text-muted-foreground text-xs underline"
              disabled={undo.pending}
              onClick={() => {
                if (window.confirm(t("accBank.rebuild.confirmUndo"))) {
                  undo.mutate(run.id);
                }
              }}
            >
              {t("accBank.rebuild.undo")}
            </button>
          </div>
        ) : (
          <p className="text-muted-foreground">{t("accBank.rebuild.notYet")}</p>
        )}
        {run && state && state.unconfirmed.length > 0 && (
          <>
            <p className="font-medium">
              {t("accBank.rebuild.unconfirmed", { count: state.unconfirmed.length })}
            </p>
            <table className="w-full">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accBank.rebuild.document")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accBank.rebuild.total")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accBank.rebuild.fakturoidPayment")}</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {state.unconfirmed.map((doc) => (
                  <tr key={`${doc.kind}-${doc.id}`} className="border-t align-top">
                    <td className="py-1 pr-4">
                      {doc.kind === "received"
                        ? t("accBank.doc.expense")
                        : t("accBank.doc.invoice")}{" "}
                      {doc.number}
                      <div className="text-muted-foreground text-xs">{doc.partnerName}</div>
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {money(doc.total, doc.currency)}
                    </td>
                    <td className="py-1 pr-4 text-xs">
                      {doc.fakturoidPayments.map((p) => (
                        <div key={`${p.paidOn}-${p.amount}`}>
                          <LocalDate value={p.paidOn} /> · {money(p.amount, doc.currency)}
                        </div>
                      ))}
                    </td>
                    <td className="space-y-1 py-1">
                      {doc.bankCandidates.length === 0 && (
                        <p className="text-muted-foreground text-xs">
                          {t("accBank.rebuild.noFit")}
                        </p>
                      )}
                      {doc.bankCandidates.map((m) => (
                        <div key={m.transactionId} className="flex flex-wrap items-center gap-2">
                          <Button
                            size="sm"
                            disabled={pair.pending}
                            onClick={() =>
                              pair.mutate({
                                kind: doc.kind,
                                documentId: doc.id,
                                ids: m.parts?.map((p) => p.transactionId) ?? [m.transactionId],
                              })
                            }
                          >
                            {t("accBank.rebuild.pair")}
                          </Button>
                          <span className="text-xs">
                            <LocalDate value={m.bookedOn} /> · {money(m.amount, m.currency)}
                            {m.parts && t("accBank.rebuild.payments", { count: m.parts.length })} ·{" "}
                            {m.card ? m.card.merchant : (m.payee ?? "—")}
                          </span>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={accept.pending}
                        onClick={() => {
                          if (window.confirm(t("accBank.rebuild.confirmKeep"))) {
                            accept.mutate({ runId: run.id, kind: doc.kind, documentId: doc.id });
                          }
                        }}
                      >
                        {t("accBank.rebuild.paidOutside")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-muted-foreground text-xs">{t("accBank.rebuild.hint")}</p>
          </>
        )}
        {run && state && state.accepted.length > 0 && (
          <div className="space-y-1">
            <p className="font-medium">
              {t("accBank.rebuild.accepted", { count: state.accepted.length })}
            </p>
            {state.accepted.map((doc) => (
              <div key={`${doc.kind}-${doc.id}`} className="flex flex-wrap items-center gap-2">
                <span>
                  {doc.kind === "received" ? t("accBank.doc.expense") : t("accBank.doc.invoice")}{" "}
                  {doc.number} · {doc.partnerName} · {money(doc.total, doc.currency)}
                </span>
                <button
                  type="button"
                  className="text-muted-foreground text-xs underline"
                  disabled={unaccept.pending}
                  onClick={() =>
                    unaccept.mutate({ runId: run.id, kind: doc.kind, documentId: doc.id })
                  }
                >
                  {t("accBank.rebuild.undoAccepted")}
                </button>
              </div>
            ))}
          </div>
        )}
        {(rebuild.error || accept.error || undo.error || unaccept.error || pair.error) && (
          <p className="text-destructive">
            {rebuild.error ?? accept.error ?? undo.error ?? unaccept.error ?? pair.error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
